import { ClockPickerView } from '@mui/x-date-pickers';
import {
  AvailabilityType,
  BookingSlot,
  Interval,
  LenderAvailability,
  Product,
  RecurringInterval,
  ShoppingCartItem,
  Unavailability,
  UnavailabilityType,
  Unit,
  Weekday
} from '@thro-types/leihbar';

import {
  add,
  addDays,
  addHours,
  addMinutes,
  format,
  getDay,
  isAfter,
  isBefore,
  isWithinInterval,
  max,
  startOfDay,
  subMinutes
} from 'date-fns';
import { enUS } from 'date-fns/locale';
import i18next from 'i18next';

import { AvailabeUnitsReactionTime } from './leihbar.types';

const numericToWeekdayMap: Record<number, Weekday> = {
  0: Weekday.SUN,
  1: Weekday.MON,
  2: Weekday.TUE,
  3: Weekday.WED,
  4: Weekday.THU,
  5: Weekday.FRI,
  6: Weekday.SAT
};

export const formatTimeFromMinutes = (minutes: number) => {
  const hours = Math.floor(minutes / 60);
  const remainingMinutes = minutes % 60;
  const baseDate = new Date();
  baseDate.setHours(hours);
  baseDate.setMinutes(remainingMinutes);
  return format(baseDate, `HH:mm ${i18next.language === 'en' ? 'aaa' : ''}`);
};

export function compareWeekdays(a: Weekday, b: Weekday): number {
  const indexA = Object.values(Weekday).indexOf(a);
  const indexB = Object.values(Weekday).indexOf(b);
  return indexA - indexB;
}

export const getMaxBookingDuration = (product: {
  maxBooking?: Pick<Interval, 'unit' | 'value'>;
}) => {
  let durationToChange = {};
  switch (product.maxBooking?.unit) {
    case Unit.MINUTE:
      durationToChange = { minutes: product.maxBooking.value };
      break;
    case Unit.HOUR:
      durationToChange = { hours: product.maxBooking.value };
      break;
    case Unit.DAY:
      durationToChange = { days: product.maxBooking.value };
      break;
    default:
      break;
  }
  return durationToChange;
};

const shouldDisableDateByLenderAvailability = (
  lenderAvailability: Pick<RecurringInterval, 'weekdays'>[] | undefined,
  day: Date
) => {
  // Check if any lender availability matches the day of the week
  return !lenderAvailability?.some(ri => ri.weekdays.includes(numericToWeekdayMap[getDay(day)]));
};

/**
 * Disables dates based on availability type or lender availability.
 *
 * @param {Date} day - The day to check.
 * @param {Product} product - The product to check.
 * @param {'START' | 'END'} type - The type of date ('START' or 'END').
 * @param {RecurringInterval[] | undefined} lenderAvailability - The lender's availability.
 * @param {ShoppingCartItem | undefined} values - Optional values (e.g., start time).
 * @returns {boolean} Whether the date should be disabled.
 */
export function shouldDisableDateByAvailabilityTypeOrLenderAvailability(
  day: Date,
  product: Pick<Product, 'availabilityType' | 'isIndependentOfLenderTimes'> & {
    possibleBookingSlots?: Pick<BookingSlot, 'start' | 'end'>[];
    recurringIntervals?: Pick<RecurringInterval, 'weekdays'>[];
    maxBooking?: Pick<Interval, 'unit' | 'value'>;
  },
  type: 'START' | 'END',
  lenderAvailability: Pick<RecurringInterval, 'weekdays'>[] | undefined,
  values?: Pick<ShoppingCartItem, 'startTime'>
): boolean {
  // Calculate the maximum end date based on the product duration
  const maxEndDate = add(values?.startTime || new Date(), getMaxBookingDuration(product));
  // Check if the day is after the maximum end date (for 'END' type)
  if (type === 'END' && isAfter(day, maxEndDate)) return true;

  // Check if the product is independent of lender times
  if (!product.isIndependentOfLenderTimes) {
    return shouldDisableDateByLenderAvailability(lenderAvailability, day);
  }

  // Check product availability type
  switch (product.availabilityType) {
    case AvailabilityType.BOOKINGSLOT:
      // Check if any booking slot includes the day
      return !product.possibleBookingSlots?.some(bs =>
        isWithinInterval(day, {
          start: startOfDay(new Date(bs.start)),
          end: new Date(bs.end)
        })
      );
    case AvailabilityType.RECURRINGINTERVAL:
      // Check if any recurring interval matches the day
      return !product.recurringIntervals?.some(ri =>
        ri.weekdays.includes(numericToWeekdayMap[getDay(day)])
      );
    default:
      // Fallthrough for all other cases
      break;
  }

  // Default behavior for non-matching cases
  return false;
}

export const handleDayUnavailabilities = (day: Date, unavailabilities: Unavailability[]) => {
  const formattedDay = format(day, 'yyyy-MM-dd');
  return isDayUnavailable(formattedDay, unavailabilities);
};

export const shouldDisableTimeByUnavailabilities = (
  time: number,
  clockType: ClockPickerView,
  selectedDateString: string | undefined,
  unavailabilities: Unavailability[],
  selectedHour: number | undefined
) => {
  if (clockType === 'hours') {
    return checkTimeUnavailability(time, clockType, selectedDateString, unavailabilities);
  } else if (clockType === 'minutes' && selectedHour) {
    return checkTimeUnavailability(
      selectedHour,
      clockType,
      selectedDateString,
      unavailabilities,
      time
    );
  }
  return false;
};

export function isDayUnavailable(dateToCheck: string, unavailabilities: Unavailability[]): boolean {
  return unavailabilities.some(
    item => item.day === dateToCheck && item.type === UnavailabilityType.DAY
  );
}

export const handleMaxPreBookingDuration = (day: Date, product: Pick<Product, 'maxPreBooking'>) => {
  if (!product.maxPreBooking) {
    return false;
  }
  const lastBookableDate = addDays(new Date(), product.maxPreBooking);
  if (isBefore(day, lastBookableDate)) {
    return false;
  } else {
    return true;
  }
};

/**
 * Checks if a specific time is unavailable based on the provided unavailability list.
 *
 * @param {number} hourToCheck - The hour to check for unavailability.
 * @param {ClockPickerView} clockType - The type of clock being used (either 'hours' or 'minutes').
 * @param {string} selectedDateString - The date string in the format used by the Unavailability type.
 * @param {Pick<Unavailability, 'day' | 'type' | 'value' | 'hour'>[]} unavailabilities - The list of unavailability information.
 * @param {number} [minute] - The minute to check for unavailability (optional, required when clockType is 'minutes').
 * @returns {boolean} True if the specified time is unavailable, false otherwise.
 */
export function checkTimeUnavailability(
  hourToCheck: number,
  clockType: ClockPickerView,
  selectedDateString: string | undefined,
  unavailabilities: Pick<Unavailability, 'day' | 'type' | 'value' | 'hour'>[],
  minute?: number
): boolean {
  if (!selectedDateString || unavailabilities.length === 0) return false;

  const isHourUnavailable = (item: Pick<Unavailability, 'day' | 'type' | 'value' | 'hour'>) =>
    item.day === selectedDateString &&
    item.type === UnavailabilityType.HOUR &&
    item.value.includes(hourToCheck);

  const isMinuteUnavailable = (item: Pick<Unavailability, 'day' | 'type' | 'value' | 'hour'>) =>
    item.day === selectedDateString &&
    item.type === UnavailabilityType.MINUTE &&
    item.hour === hourToCheck &&
    !!minute &&
    item.value.includes(minute);

  return clockType === 'hours'
    ? unavailabilities.some(isHourUnavailable)
    : !!minute && unavailabilities.some(isMinuteUnavailable);
}

export const hasUnavailabilitiesForSelectedDay = (
  selectedDateString: string,
  unavailabilities: Unavailability[]
) => {
  return unavailabilities.some(item => item.day === selectedDateString);
};
/**
 * Checks if the maximum booking duration time has been exceeded.
 *
 * @param {number} time - The current time.
 * @param {ClockPickerView} clockType - The type of clock (hours or minutes).
 * @param {Product} product - The product information.
 * @param {Date} date - The current date.
 * @param {number} selectedHour - The selected hour.
 * @param {'END' | 'START'} type - The type of booking ('END' or 'START').
 * @param {LenderAvailability | undefined} lenderAvailability - The lender's availability information.
 * @returns {boolean} True if the maximum booking duration time has been exceeded, false otherwise.
 */
export const shouldDisableTimeByMaxBookingDuration = (
  time: number,
  clockType: ClockPickerView,
  product: Product,
  date: Date,
  selectedHour: number,
  type: 'END' | 'START',
  lenderAvailability:
    | { availabilityTimes: Pick<RecurringInterval, 'weekdays' | 'start' | 'end'>[] }
    | undefined
): boolean => {
  // Function to get the maximum end date based on the product's max booking duration
  const getMaxEndDate = (day: Date) => {
    return add(day, getMaxBookingDuration(product));
  };

  // Get the current weekday
  const weekday = format(new Date(date), 'eee', { locale: enUS }).toLocaleUpperCase();
  // Check if the maximum booking duration time has been exceeded for 'END' type and hours clock
  if (clockType === 'hours' && type === 'END') {
    const selectedTime = addHours(startOfDay(date), time);
    if (isAfter(selectedTime, getMaxEndDate(date))) {
      return true; // Maximum booking duration time exceeded
    }
  }

  // Check if the product is independent of lender times and not a recurring interval
  if (product.isIndependentOfLenderTimes && product.availabilityType !== 'RECURRINGINTERVAL') {
    return false; // Maximum booking duration time not applicable
  }

  // Get the availability times based on the product's independence from lender times
  const availabilityTimes = product.isIndependentOfLenderTimes
    ? product.recurringIntervals ?? []
    : lenderAvailability?.availabilityTimes ?? [];

  // Handle time recurring intervals based on the clock type
  switch (clockType) {
    case 'hours':
      return shouldDisableHourByRecurringInterval(
        time,
        date,
        type,
        availabilityTimes,
        product,
        weekday
      );
    case 'minutes':
      return shouldDisableMinutesByRecurringInterval(
        time,
        date,
        type,
        availabilityTimes,
        product,
        weekday,
        selectedHour
      );
    default:
      return false; // Unknown clock type
  }
};

const getMaxEndDate = (day: Date, product: Product) => {
  return add(day, getMaxBookingDuration(product));
};

/**
 * This function handles the disabling of times in TimePickers for hours.
 */
const shouldDisableHourByRecurringInterval = (
  time: number,
  date: Date,
  type: 'END' | 'START',
  recurringInterval: Pick<RecurringInterval, 'weekdays' | 'start' | 'end'>[],
  product: Product,
  weekday: string
) => {
  const selectedTime = addHours(startOfDay(date), time);
  if (type === 'END' && isAfter(selectedTime, getMaxEndDate(date, product))) {
    return true;
  }
  const intervals = recurringInterval.filter(i => {
    // weekdays check is needed if weekdays have different times
    const weekdays = i.weekdays.join('');
    if (weekdays.includes(weekday)) {
      const startTime = addMinutes(startOfDay(date), i.start);
      const endTime = addMinutes(startOfDay(date), i.end);
      const isBeforeInterval = isBefore(selectedTime, subMinutes(startTime, 59));
      const isAfterInterval = isAfter(selectedTime, endTime);
      return isBeforeInterval || isAfterInterval;
    }
    return true;
  });

  if (intervals.length < (recurringInterval?.length ?? 0)) {
    return false;
  }

  return !!intervals.length;
};

/**
 * This function handles the disabling of times in TimePickers for minutes.
 */
const shouldDisableMinutesByRecurringInterval = (
  time: number,
  date: Date,
  type: 'END' | 'START',
  recurringInterval: Pick<RecurringInterval, 'weekdays' | 'start' | 'end'>[],
  product: Product,
  weekday: string,
  selectedHour: number
) => {
  const selectedTime = addMinutes(addHours(startOfDay(date), selectedHour), time);
  if (type === 'END' && isAfter(selectedTime, getMaxEndDate(date, product))) {
    return true;
  }
  const intervals = recurringInterval.filter(i => {
    // weekdays check is needed if weekdays have different times
    const weekdays = i.weekdays.join('');
    if (weekdays.includes(weekday)) {
      const startTime = addMinutes(startOfDay(date), i.start);
      const endTime = addMinutes(startOfDay(date), i.end);
      const isBeforeInterval = isBefore(selectedTime, startTime);
      const isAfterInterval = isAfter(selectedTime, endTime);
      return isBeforeInterval || isAfterInterval;
    }
    return true;
  });

  if (intervals.length < (recurringInterval?.length ?? 0)) {
    return false;
  }
  return !!intervals.length;
};

const handleReactionTime = (value: Date, product: Product) => {
  if (!product.reactionTime) return value;

  const isReactionTimeExceeded = isAfter(
    addTimeToDate(value, product.reactionTime.value, product.reactionTime.unit),
    value
  );

  return isReactionTimeExceeded
    ? value
    : addTimeToDate(value, product.reactionTime.value, product.reactionTime.unit);
};

function roundUpTo15Minutes(date: Date) {
  const minutes = date.getMinutes();
  const remainder = minutes % 15;
  if (remainder > 0) {
    date.setMinutes(minutes + (15 - remainder));
  }
  return date;
}

export const getEarliestStartTime = (
  value: Date,
  product: Product,
  lenderAvailability?: LenderAvailability
) => {
  let dateToReturn = new Date(value);

  if (product.isIndependentOfLenderTimes) {
    switch (product.availabilityType) {
      case 'ALWAYS':
        dateToReturn = product.reactionTime
          ? handleReactionTime(value, product)
          : max([roundUpTo15Minutes(new Date()), value]);
        break;
      case 'RECURRINGINTERVAL':
        for (const i of product.recurringIntervals!) {
          if (i.weekdays.includes(numericToWeekdayMap[getDay(value)])) {
            const newDate = addMinutes(startOfDay(value), i.start);
            dateToReturn = handleReactionTime(newDate, product);
            return dateToReturn;
          }
        }
        dateToReturn = handleReactionTime(value, product);
        break;
      default:
        dateToReturn = handleReactionTime(value, product);
        break;
    }
  } else {
    for (const i of lenderAvailability?.availabilityTimes!) {
      if (i.weekdays.includes(numericToWeekdayMap[getDay(value)])) {
        const newDate = addMinutes(startOfDay(value), i.start);
        dateToReturn = handleReactionTime(newDate, product);
        break;
      }
    }
  }

  return dateToReturn;
};

const addTimeToDate = (
  date: Date,
  amount: number | undefined,
  unit: keyof typeof Unit | undefined
): Date => {
  switch (unit) {
    case Unit.MINUTE:
      return add(date, { minutes: amount });
    case Unit.HOUR:
      return add(date, { hours: amount });
    case Unit.DAY:
      return add(date, { days: amount });
    default:
      return date;
  }
};

const shouldDisableByReactionTime = (day: Date, product: Product) => {
  if (!product.reactionTime) {
    return false;
  }
  const minDate = getMinDate(product);
  return isAfter(minDate, day);
};

export const doesDayHaveAtLeastOneSelectableHour = (
  day: Date,
  product: Product,
  startOrEnd: 'START' | 'END',
  lenderAvailability: LenderAvailability | undefined,
  unavailabilities: Unavailability[]
) => {
  for (let i = 0; i < 24; i++) {
    const formattedDay = format(day, 'yyyy-MM-dd');
    const isDisabledByMaxBookingDuration = shouldDisableTimeByMaxBookingDuration(
      i,
      'hours',
      product,
      day,
      i,
      startOrEnd,
      lenderAvailability
    );
    const isDisabledByUnavailabilities = shouldDisableTimeByUnavailabilities(
      i,
      'hours',
      formattedDay,
      unavailabilities,
      i
    );

    if (!(isDisabledByMaxBookingDuration || isDisabledByUnavailabilities)) {
      return true; // found at least one selectable hour
    }
  }
  return false; // no selectable hours found
};

export const shouldDisableDate = (
  day: Date,
  product: Product,
  lenderAvailability: LenderAvailability | undefined,
  unavailabilities: Unavailability[] = [],
  startOrEnd: 'START' | 'END',
  values?: ShoppingCartItem
) => {
  const availabilityTypeOrLenderAvailability =
    shouldDisableDateByAvailabilityTypeOrLenderAvailability(
      day,
      product,
      startOrEnd,
      lenderAvailability?.availabilityTimes,
      values
    );
  const dayUnavailabilities = handleDayUnavailabilities(day, unavailabilities);
  const maxPreBookingDuration = handleMaxPreBookingDuration(day, product);
  const reactionTime = shouldDisableByReactionTime(day, product);
  const hasAtLeastOneSelectableHour = doesDayHaveAtLeastOneSelectableHour(
    day,
    product,
    startOrEnd,
    lenderAvailability,
    unavailabilities
  );

  return (
    availabilityTypeOrLenderAvailability ||
    dayUnavailabilities ||
    maxPreBookingDuration ||
    reactionTime ||
    !hasAtLeastOneSelectableHour
  );
};

export function getMinDate(product: Product): Date {
  let date = new Date();

  if (!product.reactionTime) {
    return date;
  }
  const unit = product.reactionTime.unit;
  const value = product.reactionTime.value;

  switch (unit) {
    case AvailabeUnitsReactionTime.DAY:
      return addDays(new Date(), value);
    case AvailabeUnitsReactionTime.HOUR:
      return addHours(new Date(), value);
    case AvailabeUnitsReactionTime.MINUTE:
      return addMinutes(new Date(), value);
    default:
      throw new Error(`Unsupported reaction time unit: ${unit}`);
  }
}
